import { useCallback } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';

import type {
  ApiError,
  ApiVaultPreferences,
  ApiVaultPreferencesUpdate,
  ApiVaultTransferUpdateContact,
  ApiVaultTransferUpdateHeirs,
  VaultApi,
} from '@izimi/api';
import { THIRTY_MINUTES_MS } from '@izimi/utils';

import { useInvalidateDocumentsVaultListCache } from '../documents';
import { useSetVaultPreferencesCache, vaultQueryKeys } from './cache';
import {
  getContactsDocumentSelectionForHeirsSelection,
  mapVaultInfoToEntity,
  mapVaultPreferencesToEntity,
} from './helpers';

const createVaultModule = ({ vaultApi }: { vaultApi: VaultApi }) => {
  const useVaultInfo = () => {
    const { data, isLoading, error, refetch } = useQuery({
      queryKey: vaultQueryKeys.getVaultInfo(),
      queryFn: () => vaultApi.getVaultInfo().then(mapVaultInfoToEntity),
      staleTime: THIRTY_MINUTES_MS,
    });

    return {
      vaultInfo: data,
      error,
      isLoading,
      refetch,
    };
  };

  const useVaultPreferences = () => {
    const { data, isLoading, error, refetch } = useQuery({
      queryKey: vaultQueryKeys.getPreferences(),
      queryFn: () => vaultApi.getPreferences().then(mapVaultPreferencesToEntity),
      staleTime: THIRTY_MINUTES_MS,
    });

    return {
      vaultPreferences: data,
      error,
      isLoading,
      refetch,
    };
  };

  const useUpdateVaultPreferences = () => {
    const setVaultPreferencesCache = useSetVaultPreferencesCache();
    const invalidateDocumentsVaultListCache = useInvalidateDocumentsVaultListCache();

    const { mutateAsync, isPending, error } = useMutation<
      ApiVaultPreferences,
      ApiError,
      ApiVaultPreferencesUpdate
    >({
      mutationFn: (values) => vaultApi.updatePreferences(values),
      onSuccess(preferences) {
        setVaultPreferencesCache(mapVaultPreferencesToEntity(preferences));
        invalidateDocumentsVaultListCache();
      },
    });

    return {
      updateVaultPreferences: mutateAsync,
      isPending,
      error,
    };
  };

  const useUpdateVaultPreferencesTransferTarget = () => {
    const { updateVaultPreferences, error, isPending } = useUpdateVaultPreferences();

    const updateVaultPreferencesTransferTarget = useCallback(
      (value: boolean) =>
        updateVaultPreferences({
          afterDeath: {
            transferTarget: value ? 'HEIRS_AND_CONTACTS' : 'NOBODY',
            heirsDocumentSelection: {
              selectionType: 'ALL',
            },
          },
        }),
      [updateVaultPreferences],
    );

    return {
      updateVaultPreferencesTransferTarget,
      isPending,
      error,
    };
  };

  const useUpdateVaultPreferencesDelete = () => {
    const { vaultPreferences } = useVaultPreferences();
    const { updateVaultPreferences, error, isPending } = useUpdateVaultPreferences();

    const updateVaultPreferencesDelete = useCallback(
      (recipientId: string) => {
        if (!vaultPreferences) {
          throw new Error('Attempted to delete vault transfer, without data');
        }
        const { heirsDocumentSelection } = vaultPreferences.afterDeath;
        const filteredContacts: Array<ApiVaultTransferUpdateContact> =
          vaultPreferences.afterDeath.contactsDocumentSelection
            .map((c) => {
              return {
                userId: c.contact.userId,
                selectionType: c.documentSelectionType,
                documents: c.documents.map((d) => d.documentId),
              };
            })
            .filter((c) => c.userId !== recipientId);

        const newHeirsDocumentSelection: ApiVaultTransferUpdateHeirs = {
          selectionType: vaultPreferences?.isHeirsAll ? 'ALL' : 'SPECIFIC_SELECTION',
          documents: heirsDocumentSelection.documents.map((d) => d.documentId),
        };

        return updateVaultPreferences({
          afterDeath: {
            transferTarget: 'HEIRS_AND_CONTACTS',
            heirsDocumentSelection: newHeirsDocumentSelection,
            contactsDocumentSelection: filteredContacts,
          },
        });
      },
      [vaultPreferences, updateVaultPreferences],
    );

    return {
      updateVaultPreferencesDelete,
      isPending,
      error,
    };
  };

  const useUpdateVaultPreferencesSaveWholeVault = () => {
    const { vaultPreferences } = useVaultPreferences();
    const { updateVaultPreferences, isPending, error } = useUpdateVaultPreferences();

    const updateVaultPreferencesSaveWholeVault = useCallback(
      (recipientId?: string) => {
        if (!vaultPreferences) {
          throw new Error('Attempted to save vault transfer whole vault, without data');
        }
        const oldContactsDocumentSelection: Array<ApiVaultTransferUpdateContact> =
          vaultPreferences.afterDeath.contactsDocumentSelection.map((c) => {
            return {
              userId: c.contact.userId,
              selectionType: c.documentSelectionType,
              documents: c.documents.map((d) => d.documentId),
            };
          });
        let newContactsDocumentSelection: Array<ApiVaultTransferUpdateContact> = [];
        if (recipientId) {
          const isExistingContact = oldContactsDocumentSelection.some(
            (c) => c.userId === recipientId,
          );
          if (isExistingContact) {
            newContactsDocumentSelection = oldContactsDocumentSelection.map((c) =>
              c.userId === recipientId ? { ...c, selectionType: 'ALL', documents: undefined } : c,
            );
          } else {
            newContactsDocumentSelection = [
              ...oldContactsDocumentSelection,
              {
                userId: recipientId,
                selectionType: 'ALL',
                documents: undefined,
              },
            ];
          }
        }
        return updateVaultPreferences({
          afterDeath: {
            transferTarget: 'HEIRS_AND_CONTACTS',
            contactsDocumentSelection: recipientId
              ? newContactsDocumentSelection
              : oldContactsDocumentSelection,
            heirsDocumentSelection: { selectionType: 'ALL' },
          },
        });
      },
      [vaultPreferences, updateVaultPreferences],
    );

    return {
      updateVaultPreferencesSaveWholeVault,
      isPending,
      error,
    };
  };

  const useUpdateVaultPreferencesSaveSpecific = () => {
    const { vaultPreferences } = useVaultPreferences();
    const { updateVaultPreferences, isPending, error } = useUpdateVaultPreferences();

    const updateVaultPreferencesSaveSpecific = useCallback(
      (selectedDocumentIds: Array<string>, recipientId?: string) => {
        if (!vaultPreferences) {
          throw new Error('Attempted to save vault transfer specific, without data');
        }
        const oldContactsDocumentSelection: Array<ApiVaultTransferUpdateContact> =
          vaultPreferences.afterDeath.contactsDocumentSelection.map((c) => {
            return {
              userId: c.contact.userId,
              selectionType: c.documentSelectionType,
              documents: c.documents.map((d) => d.documentId),
            };
          });

        if (!recipientId) {
          const newContactsDocumentSelection: Array<ApiVaultTransferUpdateContact> =
            getContactsDocumentSelectionForHeirsSelection(
              vaultPreferences.afterDeath.contactsDocumentSelection,
              selectedDocumentIds,
            );

          return updateVaultPreferences({
            afterDeath: {
              transferTarget: 'HEIRS_AND_CONTACTS',
              contactsDocumentSelection: newContactsDocumentSelection.filter(
                (c) => c.documents?.length && c.documents.length > 0,
              ),
              heirsDocumentSelection: {
                selectionType: 'SPECIFIC_SELECTION',
                documents: selectedDocumentIds,
              },
            },
          });
        }
        const newHeirsDocumentSelection: ApiVaultTransferUpdateHeirs = {
          selectionType: vaultPreferences?.isHeirsAll ? 'ALL' : 'SPECIFIC_SELECTION',
          documents: vaultPreferences?.isHeirsAll
            ? []
            : [...vaultPreferences.oldHeirsDocumentIds, ...selectedDocumentIds],
        };
        const isExistingContact = oldContactsDocumentSelection.some(
          (c) => c.userId === recipientId,
        );

        const updatedContact: ApiVaultTransferUpdateContact = {
          userId: recipientId,
          selectionType: 'SPECIFIC_SELECTION',
          documents: selectedDocumentIds,
        };

        const contactsDocumentSelection: Array<ApiVaultTransferUpdateContact> = isExistingContact
          ? oldContactsDocumentSelection.map((c) => (c.userId === recipientId ? updatedContact : c))
          : [...oldContactsDocumentSelection, updatedContact];

        return updateVaultPreferences({
          afterDeath: {
            transferTarget: 'HEIRS_AND_CONTACTS',
            heirsDocumentSelection: newHeirsDocumentSelection,
            contactsDocumentSelection,
          },
        });
      },
      [vaultPreferences, updateVaultPreferences],
    );

    return {
      updateVaultPreferencesSaveSpecific,
      isPending,
      error,
    };
  };

  return {
    useVaultInfo,
    useVaultPreferences,
    useUpdateVaultPreferencesSaveWholeVault,
    useUpdateVaultPreferencesSaveSpecific,
    useUpdateVaultPreferencesTransferTarget,
    useUpdateVaultPreferencesDelete,
  };
};

export default createVaultModule;
